import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useForge } from '../data/useForge'
import { useI18n } from '../i18n/useI18n'
import { Icon } from '../components/Icon'
import { Layout } from '../components/Layout'

export default function WorkflowDetail() {
  const { id } = useParams<{ id: string }>()
  const { t, lang } = useI18n()
  const { registry, selectWorkflow } = useForge()

  const workflow = useMemo(() => {
    return registry?.workflows.find((w) => w.id === id) ?? null
  }, [registry, id])

  if (!workflow || !registry) {
    return (
      <Layout>
        <div className="px-4 py-24 text-center md:px-margin-desktop">
          <Icon name="search" size={40} className="mx-auto mb-4 text-outline" />
          <h1 className="font-display-lg text-display-lg text-on-surface">Workflow not found</h1>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="px-4 py-12 md:px-margin-desktop">
        <section className="mb-12 flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-outline-variant bg-surface-container-high px-3 py-1 font-label-sm text-label-sm text-on-surface-variant">
              {t.workflowDetail.advancedWorkflow}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-tertiary-container px-3 py-1 font-label-sm text-label-sm text-on-tertiary-container">
              <Icon name="signal_cellular_alt" size={16} />
              {t.workflowDetail.highComplexity}
            </span>
          </div>

          <h1 className="max-w-3xl font-display-lg text-display-lg text-on-surface">
            {lang === 'zh' ? workflow.nameZh : workflow.name}
          </h1>

          <p className="max-w-2xl font-body-lg text-body-lg leading-relaxed text-on-surface-variant">
            {lang === 'zh' ? workflow.descriptionZh : workflow.description}
          </p>

          <div className="mt-4 flex gap-4">
            <button
              type="button"
              onClick={() => selectWorkflow(workflow.id)}
              className="flex items-center gap-2 rounded bg-primary px-6 py-3 font-label-sm text-label-sm font-bold text-on-primary shadow-forge-sm transition-colors hover:bg-surface-tint"
            >
              <Icon name="play_arrow" size={18} />
              {t.workflowDetail.instantiate}
            </button>
            <button
              type="button"
              className="flex items-center gap-2 rounded border border-outline bg-surface px-6 py-3 font-label-sm text-label-sm text-on-surface transition-colors hover:bg-surface-container-low"
            >
              <Icon name="bookmark_border" size={18} />
              {t.workflowDetail.saveToLibrary}
            </button>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <section className="rounded-xl border border-surface-container-high bg-surface-container-lowest p-8">
              <h2 className="mb-8 border-b border-surface-container-high pb-4 font-headline-lg text-headline-lg text-on-surface">
                {t.workflowDetail.executionPathway}
              </h2>

              <div className="relative flex flex-col gap-10 border-l border-outline-variant pl-8">
                {workflow.steps.map((step, index) => {
                  const isFirst = index === 0
                  return (
                    <div key={index} className="relative">
                      <div
                        className={`absolute -left-[41px] top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                          isFirst ? 'border-primary' : 'border-outline-variant'
                        } bg-surface`}
                      >
                        {isFirst && <div className="h-2 w-2 rounded-full bg-primary" />}
                      </div>

                      <h3 className="mb-2 font-headline-md text-headline-md text-on-surface">
                        Step {index + 1}: {lang === 'zh' ? step.titleZh : step.title}
                      </h3>
                      <p className="mb-4 font-body-md text-body-md text-on-surface-variant">
                        {lang === 'zh' ? step.descriptionZh : step.description}
                      </p>

                      {step.skillId && (
                        <div className="mb-3">
                          {(() => {
                            const skill = registry.skills.find((s) => s.id === step.skillId)
                            return skill ? (
                              <span className="inline-flex items-center gap-1 rounded border border-outline-variant bg-surface-container px-2 py-1 font-label-sm text-label-sm text-on-surface-variant">
                                <Icon name="neurology" size={14} />
                                {lang === 'zh' ? skill.nameZh : skill.name}
                              </span>
                            ) : null
                          })()}
                        </div>
                      )}

                      {step.code && (
                        <div className="overflow-x-auto rounded border border-[#30312d] bg-inverse-surface p-4 code-active-border"
                        >
                          <pre className="font-code-md text-code-md text-surface-container-high">
                            <code>{step.code}</code>
                          </pre>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          </div>

          <div className="flex flex-col gap-8">
            <section className="rounded-xl border border-surface-container-high bg-surface p-6 shadow-forge-sm"
            >
              <h2 className="mb-4 border-b border-surface-container-high pb-2 font-headline-md text-headline-md text-on-surface"
              >
                {t.workflowDetail.requiredSkills}
              </h2>
              <ul className="flex flex-col gap-3">
                {workflow.skillIds.map((skillId) => {
                  const skill = registry.skills.find((s) => s.id === skillId)
                  if (!skill) return null
                  return (
                    <li
                      key={skillId}
                      className="flex items-start gap-3 border-b border-surface-container-lowest py-2 last:border-0"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-secondary-container text-on-secondary-container"
                      >
                        <Icon name={skill.icon} size={18} />
                      </div>
                      <div>
                        <h4 className="font-label-sm text-label-sm font-bold text-on-surface"
                        >
                          {lang === 'zh' ? skill.nameZh : skill.name}
                        </h4>
                        <p className="mt-1 text-xs text-on-surface-variant">{skill.sourceRepo}</p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </section>

            <section className="rounded-xl border border-surface-container-high bg-surface p-6 shadow-forge-sm"
            >
              <h2 className="mb-4 border-b border-surface-container-high pb-2 font-headline-md text-headline-md text-on-surface"
              >
                {t.workflowDetail.expectedOutputs}
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {workflow.outputs.map((output, i) => (
                  <div
                    key={i}
                    className="group relative aspect-[3/4] cursor-pointer overflow-hidden rounded border border-outline-variant"
                  >
                    <div
                      className={`absolute inset-0 ${
                        i % 2 === 0
                          ? 'bg-gradient-to-br from-surface-container-high to-surface-container-lowest'
                          : 'bg-gradient-to-br from-secondary-container to-surface-container-lowest opacity-50'
                      }`}
                    />
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center"
                    >
                      <Icon
                        name={output.endsWith('.pdf') ? 'picture_as_pdf' : 'bar_chart'}
                        size={36}
                        className="mb-2 text-on-surface-variant"
                      />
                      <span className="px-2 text-center font-label-sm text-label-sm text-on-surface"
                      >
                        {output}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-xl border border-outline-variant bg-surface-container-low p-6"
            >
              <h2 className="mb-2 font-headline-md text-headline-md text-on-surface"
              >
                {t.workflowDetail.limitations}
              </h2>
              <p className="font-body-md text-body-md text-on-surface-variant"
              >
                {lang === 'zh' ? workflow.limitationsZh : workflow.limitations}
              </p>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  )
}
